import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CpuIcon, MonitorIcon, PrinterIcon, ScanLineIcon, WifiIcon, WifiOffIcon, RefreshCwIcon, CheckCircle2Icon, XCircleIcon, AlertTriangleIcon } from 'lucide-react';
interface HardwareDevice {
  id: string;
  name: string;
  type: 'barrier' | 'printer' | 'scanner' | 'display' | 'controller';
  location: string;
  ipAddress: string;
  status: 'online' | 'offline' | 'warning';
  lastSeen: string;
  uptime: string;
  details: string;
}
const mockDevices: HardwareDevice[] = [{
  id: 'HW-001',
  name: 'Entry Barrier Gate',
  type: 'barrier',
  location: 'Main Entry',
  ipAddress: '192.168.1.101',
  status: 'online',
  lastSeen: '2026-04-15 14:32:10',
  uptime: '15d 8h 22m',
  details: 'Boom barrier – Auto mode'
}, {
  id: 'HW-002',
  name: 'Exit Barrier Gate',
  type: 'barrier',
  location: 'Main Exit',
  ipAddress: '192.168.1.102',
  status: 'online',
  lastSeen: '2026-04-15 14:32:08',
  uptime: '15d 8h 20m',
  details: 'Boom barrier – Auto mode'
}, {
  id: 'HW-003',
  name: 'Thermal Printer (Entry)',
  type: 'printer',
  location: 'Entry Booth',
  ipAddress: '192.168.1.201',
  status: 'online',
  lastSeen: '2026-04-15 14:31:55',
  uptime: '3d 2h 10m',
  details: '80mm POS – Paper OK'
}, {
  id: 'HW-004',
  name: 'Thermal Printer (Exit)',
  type: 'printer',
  location: 'Exit Booth',
  ipAddress: '192.168.1.202',
  status: 'warning',
  lastSeen: '2026-04-15 14:30:12',
  uptime: '3d 2h 8m',
  details: '80mm POS – Paper Low'
}, {
  id: 'HW-005',
  name: 'RFID Scanner (Entry)',
  type: 'scanner',
  location: 'Main Entry',
  ipAddress: '192.168.1.110',
  status: 'online',
  lastSeen: '2026-04-15 14:32:11',
  uptime: '10d 5h 44m',
  details: 'UHF RFID Reader'
}, {
  id: 'HW-006',
  name: 'RFID Scanner (Exit)',
  type: 'scanner',
  location: 'Main Exit',
  ipAddress: '192.168.1.111',
  status: 'offline',
  lastSeen: '2026-04-15 12:05:33',
  uptime: '—',
  details: 'UHF RFID Reader – Connection lost'
}, {
  id: 'HW-007',
  name: 'LED Display Board',
  type: 'display',
  location: 'Main Entry',
  ipAddress: '192.168.1.150',
  status: 'online',
  lastSeen: '2026-04-15 14:32:00',
  uptime: '30d 12h 5m',
  details: 'P10 Outdoor LED – Showing capacity'
}, {
  id: 'HW-008',
  name: 'PLC Controller',
  type: 'controller',
  location: 'Server Room',
  ipAddress: '192.168.1.50',
  status: 'online',
  lastSeen: '2026-04-15 14:32:12',
  uptime: '45d 0h 15m',
  details: 'Main PLC – All I/O normal'
}];
const typeIcons: Record<HardwareDevice['type'], React.ElementType> = {
  barrier: CpuIcon,
  printer: PrinterIcon,
  scanner: ScanLineIcon,
  display: MonitorIcon,
  controller: CpuIcon
};
const statusConfig = {
  online: {
    icon: CheckCircle2Icon,
    label: 'Online',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    border: 'border-emerald-200'
  },
  offline: {
    icon: XCircleIcon,
    label: 'Offline',
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
    border: 'border-red-200'
  },
  warning: {
    icon: AlertTriangleIcon,
    label: 'Warning',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
    border: 'border-amber-200'
  }
};
export function HardwareStatusPage() {
  const [devices, setDevices] = useState<HardwareDevice[]>(mockDevices);
  const [refreshing, setRefreshing] = useState(false);
  const onlineCount = devices.filter(d => d.status === 'online').length;
  const offlineCount = devices.filter(d => d.status === 'offline').length;
  const warningCount = devices.filter(d => d.status === 'warning').length;
  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      setDevices([...mockDevices]);
      setRefreshing(false);
    }, 1200);
  };
  return <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-id="element-664">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-665">
        <div data-id="element-666">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-667">Hardware Status</h1>
          <p className="text-sm text-slate-500 mt-1" data-id="element-668">
            Real-time status of all connected hardware devices
          </p>
        </div>
        <button onClick={handleRefresh} disabled={refreshing} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium disabled:opacity-60" data-id="element-669">
          <RefreshCwIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} data-id="element-670" />
          {refreshing ? 'Refreshing...' : 'Refresh Status'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-671">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4" data-id="element-672">
          <div className="p-3 bg-emerald-50 rounded-lg" data-id="element-673">
            <WifiIcon className="w-6 h-6 text-emerald-600" data-id="element-674" />
          </div>
          <div data-id="element-675">
            <p className="text-2xl font-bold text-slate-800" data-id="element-676">{onlineCount}</p>
            <p className="text-sm text-slate-500" data-id="element-677">Online</p>
          </div>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4" data-id="element-678">
          <div className="p-3 bg-amber-50 rounded-lg" data-id="element-679">
            <AlertTriangleIcon className="w-6 h-6 text-amber-600" data-id="element-680" />
          </div>
          <div data-id="element-681">
            <p className="text-2xl font-bold text-slate-800" data-id="element-682">{warningCount}</p>
            <p className="text-sm text-slate-500" data-id="element-683">Warning</p>
          </div>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4" data-id="element-684">
          <div className="p-3 bg-red-50 rounded-lg" data-id="element-685">
            <WifiOffIcon className="w-6 h-6 text-red-600" data-id="element-686" />
          </div>
          <div data-id="element-687">
            <p className="text-2xl font-bold text-slate-800" data-id="element-688">{offlineCount}</p>
            <p className="text-sm text-slate-500" data-id="element-689">Offline</p>
          </div>
        </motion.div>
      </div>

      {/* Devices Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-690">
        <div className="overflow-x-auto" data-id="element-691">
          <table className="w-full text-sm" data-id="element-692">
            <thead data-id="element-693">
              <tr className="bg-slate-50 border-b border-slate-200" data-id="element-694">
                <th className="text-left px-4 py-3 font-semibold text-slate-600" data-id="element-695">
                  Device
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600" data-id="element-696">
                  Location
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600" data-id="element-697">
                  IP Address
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600" data-id="element-698">
                  Status
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600" data-id="element-699">
                  Uptime
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600" data-id="element-700">
                  Last Seen
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600" data-id="element-701">
                  Details
                </th>
              </tr>
            </thead>
            <tbody data-id="element-702">
              {devices.map((device, idx) => {
              const TypeIcon = typeIcons[device.type];
              const sc = statusConfig[device.status];
              const StatusIcon = sc.icon;
              return <motion.tr key={device.id} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: idx * 0.03
              }} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 transition-colors" data-id="element-703">
                    <td className="px-4 py-3" data-id="element-704">
                      <div className="flex items-center gap-3" data-id="element-705">
                        <div className="p-1.5 bg-slate-100 rounded-lg" data-id="element-706">
                          <TypeIcon className="w-4 h-4 text-slate-600" data-id="element-707" />
                        </div>
                        <div data-id="element-708">
                          <p className="font-medium text-slate-800" data-id="element-709">
                            {device.name}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-710">{device.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600" data-id="element-711">
                      {device.location}
                    </td>
                    <td className="px-4 py-3" data-id="element-712">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono text-slate-600" data-id="element-713">
                        {device.ipAddress}
                      </code>
                    </td>
                    <td className="px-4 py-3" data-id="element-714">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.text} border ${sc.border}`} data-id="element-715">
                        <StatusIcon className="w-3.5 h-3.5" data-id="element-716" />
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs" data-id="element-717">
                      {device.uptime}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs" data-id="element-718">
                      {device.lastSeen}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs" data-id="element-719">
                      {device.details}
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}